/**
 * SectionTabs — the route-linked tab strip under the header for a sidebar entry
 * that covers several screens (Groups, Photos, Jaguar-Owned…). See
 * docs/admin-ia-revamp-plan.md §2.
 *
 * Deliberately NOT antd <Tabs>: every tab is its own existing route, so each one
 * is a plain <Link>. Nothing about a page changes — its URL, deep links, back
 * button and breadcrumb all behave exactly as before; the strip is only a way to
 * find the neighbouring screens. (The Mentorship hub's in-page antd tabs are a
 * different thing and stay as they are.)
 *
 * Renders nothing when the current route isn't part of a tabbed entry.
 */
import React from 'react';
import { Link, useLocation } from 'react-router';
import { brand } from '../theme/brand';
import { ADMIN_NAV, activeNav, type AdminNavGroup } from './adminNav';

export function SectionTabs({ nav = ADMIN_NAV }: { nav?: AdminNavGroup[] }) {
  const { pathname } = useLocation();
  const active = activeNav(pathname, nav);
  const tabs = active?.item.tabs;
  if (!active || !tabs || tabs.length < 2) return null;

  return (
    <nav
      aria-label={`${active.item.label} sections`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        padding: '0 28px',
        borderBottom: '1px solid ' + brand.line,
        background: '#fff',
      }}
    >
      {tabs.map((tab) => {
        const on = tab.to === active.to;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            aria-current={on ? 'page' : undefined}
            style={{
              fontFamily: brand.fSans,
              fontWeight: on ? 700 : 500,
              fontSize: 13,
              padding: '10px 12px 9px',
              marginBottom: -1,
              textDecoration: 'none',
              color: on ? brand.ink : brand.sub,
              borderBottom: `2px solid ${on ? brand.gold : 'transparent'}`,
            }}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
